export function drawRotateGrip(ctx, x, y, fillColor) {
  ctx.save();
  ctx.beginPath(); ctx.arc(x, y, 9, 0, Math.PI * 2);
  ctx.fillStyle = fillColor; ctx.fill();
  ctx.strokeStyle = "#fff"; ctx.lineWidth = 2; ctx.stroke();
  ctx.translate(x, y);
  ctx.strokeStyle = "#fff"; ctx.fillStyle = "#fff";
  ctx.lineWidth = 1.4; ctx.lineCap = "round"; ctx.lineJoin = "round";
  const r = 4.2, start = -Math.PI * 0.95, end = Math.PI * 0.55;
  ctx.beginPath(); ctx.arc(0, 0, r, start, end); ctx.stroke();
  const ex = r * Math.cos(end), ey = r * Math.sin(end);
  const tangent = end + Math.PI / 2;
  ctx.beginPath();
  ctx.moveTo(ex + 3 * Math.cos(tangent + 2.6), ey + 3 * Math.sin(tangent + 2.6));
  ctx.lineTo(ex, ey);
  ctx.lineTo(ex + 3 * Math.cos(tangent - 2.6), ey + 3 * Math.sin(tangent - 2.6));
  ctx.stroke();
  ctx.restore();
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getWrappedTextLines(ctx, text, maxWrapWidth) {
  if (!text) return [""];
  const rawParagraphs = String(text).split("\n");
  const result = [];
  for (const para of rawParagraphs) {
    if (!para) {
      result.push("");
      continue;
    }
    const words = para.split(" ");
    let curLine = words[0];
    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const testLine = curLine + " " + word;
      if (maxWrapWidth && maxWrapWidth > 60 && ctx.measureText(testLine).width > maxWrapWidth) {
        result.push(curLine);
        curLine = word;
      } else {
        curLine = testLine;
      }
    }
    result.push(curLine);
  }
  return result;
}

function drawArrowLine(ctx, x1, y1, x2, y2, color, thickness) {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const hl = 18 + thickness * 1.5;
  const hw = 14 + thickness * 1.0;

  const indentX = x2 - hl * 0.65 * Math.cos(angle);
  const indentY = y2 - hl * 0.65 * Math.sin(angle);

  const ux = Math.cos(angle), uy = Math.sin(angle);
  const pxp = -uy, pyp = ux;

  // Tapered shaft widths (sleek proportional sizing)
  const w_tail = Math.max(0.5, thickness * 0.25);
  const w_head = Math.max(1, thickness * 0.65);

  ctx.save();
  // Drop shadow
  ctx.shadowColor = "rgba(0,0,0,0.38)";
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 1.5;
  ctx.shadowOffsetY = 2;

  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = thickness;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Draw tapered shaft polygon
  ctx.beginPath();
  ctx.moveTo(x1 + pxp * w_tail, y1 + pyp * w_tail);
  ctx.lineTo(indentX + pxp * w_head, indentY + pyp * w_head);
  ctx.lineTo(indentX - pxp * w_head, indentY - pyp * w_head);
  ctx.lineTo(x1 - pxp * w_tail, y1 - pyp * w_tail);
  ctx.closePath();
  ctx.fill();

  // Round tail cap
  ctx.beginPath();
  ctx.arc(x1, y1, w_tail, 0, Math.PI * 2);
  ctx.fill();

  // Draw swept-back (chevron) arrowhead
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - hl * Math.cos(angle) + hw * Math.sin(angle), y2 - hl * Math.sin(angle) - hw * Math.cos(angle));
  ctx.lineTo(indentX, indentY);
  ctx.lineTo(x2 - hl * Math.cos(angle) - hw * Math.sin(angle), y2 - hl * Math.sin(angle) + hw * Math.cos(angle));
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared magnifier-lens renderer — used for both:
//   • the legacy standalone type:"magnifier" objects  (via drawMagnifier below)
//   • embedded magnifiers on arrow/line/rect/circle endpoints (via renderObj)
//
// targetX/Y  — the fixed point on the photo being magnified (the endpoint)
// lensX/Y    — position of the circular lens bubble (draggable separately)
// anchorX/Y  — the "from" point of the connecting arrow (other endpoint / shape edge)
// ─────────────────────────────────────────────────────────────────────────────
export function drawMagnifierLens(ctx, {
  targetX, targetY,
  lensX, lensY,
  anchorX, anchorY,
  lensRadius = 70,
  zoomLevel = 3,
  color = "#ffcc00",
  thickness = 5,
  imgEl,
  imgSize,
  adjustments,
  objects,
  selectedIds,
}) {
  const r = lensRadius;

  // Lens circle — shadow + clipped zoomed image
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.45)";
  ctx.shadowBlur = 12;
  ctx.shadowOffsetY = 3;
  ctx.beginPath(); ctx.arc(lensX, lensY, r, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0,0,0,0.18)"; ctx.fill();
  ctx.clip();

  ctx.shadowColor = "transparent";

  // Render the background image and annotations zoomed inside the clip path
  ctx.save();
  ctx.translate(lensX, lensY);
  ctx.scale(zoomLevel, zoomLevel);
  ctx.translate(-targetX, -targetY);

  if (imgEl) {
    const adj = adjustments || { brightness: 100, contrast: 100, saturation: 100 };
    const iw = imgSize ? imgSize.w : ctx.canvas.width;
    const ih = imgSize ? imgSize.h : ctx.canvas.height;
    ctx.filter = `brightness(${adj.brightness}%) contrast(${adj.contrast}%) saturate(${adj.saturation}%)`;
    const prevSmoothing = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = false; // Disable smoothing for sharp/pixelated HD zoom
    ctx.drawImage(imgEl, 0, 0, iw, ih);
    ctx.imageSmoothingEnabled = prevSmoothing;
    ctx.filter = "none";
  }

  if (objects) {
    const renderOptions = { imgEl, imgSize, adjustments, objects };
    objects.forEach(o => {
      if (o.type !== "magnifier") {
        renderObj(ctx, o, selectedIds && selectedIds.includes(o.id), renderOptions);
      }
    });
  }
  ctx.restore();
  ctx.restore();

  // Draw selection handles at normal, unscaled size inside the magnifier lens
  if (selectedIds && selectedIds.length === 1) {
    const sel = objects.find(o => o.id === selectedIds[0]);
    if (sel) {
      ctx.save();
      // Clip to lens circle
      ctx.beginPath(); ctx.arc(lensX, lensY, r, 0, Math.PI * 2);
      ctx.clip();

      const mapToLens = (x, y) => ({
        x: lensX + (x - targetX) * zoomLevel,
        y: lensY + (y - targetY) * zoomLevel,
      });

      if (sel.type === "arrow" || sel.type === "line") {
        [[sel.x1, sel.y1], [sel.x2, sel.y2]].forEach(([x, y]) => {
          const lp = mapToLens(x, y);
          drawHandle(ctx, lp.x, lp.y, sel.color, 8);
        });
      } else if (sel.type === "rect" || sel.type === "circle") {
        const rx1 = Math.min(sel.x1, sel.x2), ry1 = Math.min(sel.y1, sel.y2);
        const rx2 = Math.max(sel.x1, sel.x2), ry2 = Math.max(sel.y1, sel.y2);
        const corners = [
          [rx1, ry1],
          [rx2, ry1],
          [rx1, ry2],
          [rx2, ry2],
        ];
        corners.forEach(([x, y]) => {
          const lp = mapToLens(x, y);
          drawHandle(ctx, lp.x, lp.y, sel.color, 7);
        });
      }
      ctx.restore();
    }
  }

  // Lens border rings
  ctx.save();
  ctx.beginPath(); ctx.arc(lensX, lensY, r, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.92)";
  ctx.lineWidth = Math.max(2, thickness + 2); ctx.stroke();
  ctx.beginPath(); ctx.arc(lensX, lensY, r - Math.max(2, thickness / 2), 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = thickness; ctx.stroke();
  ctx.restore();
}

// ─────────────────────────────────────────────────────────────────────────────
// Mapping: handle key → sub-object key stored on the annotation.
// E.g. "p2" → "magnifierP2", "br" → "magnifierBR"
// ─────────────────────────────────────────────────────────────────────────────
export const HANDLE_TO_MAG_KEY = {
  p1: "magnifierP1",
  p2: "magnifierP2",
  tl: "magnifierTL",
  tr: "magnifierTR",
  bl: "magnifierBL",
  br: "magnifierBR",
};

// Returns the target (x,y) for a given handle key on obj
export function handleTargetPoint(obj, handleKey) {
  if (handleKey === "p1") return { x: obj.x1, y: obj.y1 };
  if (handleKey === "p2") return { x: obj.x2, y: obj.y2 };
  // rect/circle corners (b.x/y map to x1/y1 being the top-left in draw coords)
  const x1 = Math.min(obj.x1, obj.x2), y1 = Math.min(obj.y1, obj.y2);
  const x2 = Math.max(obj.x1, obj.x2), y2 = Math.max(obj.y1, obj.y2);
  if (handleKey === "tl") return { x: x1, y: y1 };
  if (handleKey === "tr") return { x: x2, y: y1 };
  if (handleKey === "bl") return { x: x1, y: y2 };
  if (handleKey === "br") return { x: x2, y: y2 };
  return { x: obj.x1, y: obj.y1 };
}

// Anchor = the OTHER point (opposite end or centre of shape edge)
export function handleAnchorPoint(obj, handleKey) {
  if (handleKey === "p1") return { x: obj.x2, y: obj.y2 };
  if (handleKey === "p2") return { x: obj.x1, y: obj.y1 };
  // For rect/circle, anchor is the centre of the shape
  return { x: (obj.x1 + obj.x2) / 2, y: (obj.y1 + obj.y2) / 2 };
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy standalone magnifier renderer (kept for backward-compat with objects
// that were created with type:"magnifier" before this refactor).
// ─────────────────────────────────────────────────────────────────────────────
function drawMagnifier(ctx, obj, options) {
  const {
    imgEl,
    imgSize = { w: ctx.canvas.width, h: ctx.canvas.height },
    adjustments = { brightness: 100, contrast: 100, saturation: 100 },
    objects,
  } = options || {};
  const r = obj.lensRadius || 70;
  const zoom = obj.zoomLevel || 3;
  const color = obj.color || "#ffcc00";
  const thickness = obj.thickness || 5;
  const anchorX = obj.anchorX ?? obj.x1 ?? 0;
  const anchorY = obj.anchorY ?? obj.y1 ?? 0;
  const targetX = obj.targetX ?? obj.x2 ?? anchorX;
  const targetY = obj.targetY ?? obj.y2 ?? anchorY;
  const lensX = obj.lensX ?? clamp(targetX + r * 0.95, r, imgSize.w - r);
  const lensY = obj.lensY ?? clamp(targetY - r * 0.95, r, imgSize.h - r);

  drawMagnifierLens(ctx, { targetX, targetY, lensX, lensY, anchorX, anchorY, lensRadius: r, zoomLevel: zoom, color, thickness, imgEl, imgSize, adjustments, objects });
}

// ─────────────────────────────────────────────────────────────────────────────
// Draw any embedded magnifier lenses attached to an annotation object.
// Called from renderObj after the base shape is drawn.
// ─────────────────────────────────────────────────────────────────────────────
function drawEmbeddedMagnifiers(ctx, obj, options) {
  const { imgEl, imgSize, adjustments, objects } = options || {};
  const color = obj.color || "#ffcc00";
  const thickness = obj.thickness || 5;

  for (const [handleKey, magKey] of Object.entries(HANDLE_TO_MAG_KEY)) {
    const mag = obj[magKey];
    if (!mag) continue;

    const target = handleTargetPoint(obj, handleKey);
    const anchor = handleAnchorPoint(obj, handleKey);

    // For arrow/line with magnifier on p2 (the arrowhead end), we want the
    // base line to stop at the lens edge, not draw through it. We do this by
    // overlaying the full lens on top of the already-drawn shape — the clipped
    // image covers the arrowhead, and the lens ring sits on top.
    drawMagnifierLens(ctx, {
      targetX: target.x,
      targetY: target.y,
      lensX: mag.lensX,
      lensY: mag.lensY,
      anchorX: anchor.x,
      anchorY: anchor.y,
      lensRadius: mag.lensRadius || 70,
      zoomLevel: mag.zoomLevel || 3,
      color,
      thickness,
      imgEl,
      imgSize,
      adjustments,
      objects,
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Returns true if obj has at least one embedded magnifier
// ─────────────────────────────────────────────────────────────────────────────
export function hasEmbeddedMagnifier(obj) {
  return Object.values(HANDLE_TO_MAG_KEY).some(k => !!obj[k]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Returns the first embedded magnifier sub-object found, or null.
// Used by the zoom slider to find which magnifier is "active" on a selected object.
// ─────────────────────────────────────────────────────────────────────────────
export function getFirstEmbeddedMagnifier(obj) {
  for (const k of Object.values(HANDLE_TO_MAG_KEY)) {
    if (obj[k]) return { key: k, ...obj[k] };
  }
  return null;
}

export function renderObj(ctx, obj, isSelected, options = {}) {
  if (obj.type === "magnifier") {
    drawMagnifier(ctx, obj, options);
    return;
  }

  ctx.save();
  const rot = obj.type === "text" ? 0 : (obj.rotation || 0);
  if (rot) {
    const b = getBounds(obj);
    if (b) {
      const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
      ctx.translate(cx, cy); ctx.rotate(rot * Math.PI / 180); ctx.translate(-cx, -cy);
    }
  }
  ctx.strokeStyle = obj.color; ctx.fillStyle = obj.color;
  ctx.lineWidth = obj.thickness || 3;
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  if (obj.type === "pen") {
    if (!obj.points || obj.points.length < 2) { ctx.restore(); return; }
    ctx.beginPath(); ctx.moveTo(obj.points[0].x, obj.points[0].y);
    obj.points.forEach(p => ctx.lineTo(p.x, p.y)); ctx.stroke();
  } else if (obj.type === "line") {
    if (obj.filled) {
      const angle = Math.atan2(obj.y2 - obj.y1, obj.x2 - obj.x1);
      const ux = Math.cos(angle), uy = Math.sin(angle);
      const pxp = -uy, pyp = ux;
      const halfW = Math.max(4, (obj.thickness || 3) * 1.8);
      ctx.beginPath();
      ctx.moveTo(obj.x1 + halfW * pxp, obj.y1 + halfW * pyp);
      ctx.lineTo(obj.x2 + halfW * pxp, obj.y2 + halfW * pyp);
      ctx.lineTo(obj.x2 - halfW * pxp, obj.y2 - halfW * pyp);
      ctx.lineTo(obj.x1 - halfW * pxp, obj.y1 - halfW * pyp);
      ctx.closePath();
      ctx.fillStyle = obj.fillColor || obj.color;
      ctx.fill();
      ctx.beginPath(); ctx.arc(obj.x1, obj.y1, halfW, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(obj.x2, obj.y2, halfW, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = obj.color;
    } else {
      ctx.save();
      ctx.lineWidth = (obj.thickness || 3) * 1.5;
      ctx.beginPath(); ctx.moveTo(obj.x1, obj.y1); ctx.lineTo(obj.x2, obj.y2); ctx.stroke();
      ctx.restore();
    }
  } else if (obj.type === "arrow") {
    const { x1, y1, x2, y2 } = obj;
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const thickness = obj.thickness || 3;
    const hl = 18 + thickness * 1.5;
    const hw = 14 + thickness * 1.0;

    const indentX = x2 - hl * 0.65 * Math.cos(angle);
    const indentY = y2 - hl * 0.65 * Math.sin(angle);

    const ux = Math.cos(angle), uy = Math.sin(angle);
    const pxp = -uy, pyp = ux;

    // Tapered shaft widths (sleek proportional sizing)
    const w_tail = Math.max(0.5, thickness * 0.25);
    const w_head = Math.max(1, thickness * 0.65);

    ctx.save();
    // Drop shadow
    ctx.shadowColor = "rgba(0,0,0,0.38)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 1.5;
    ctx.shadowOffsetY = 2;

    ctx.strokeStyle = obj.color;
    ctx.fillStyle = obj.color;
    ctx.lineWidth = thickness;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Draw tapered shaft polygon
    ctx.beginPath();
    ctx.moveTo(x1 + pxp * w_tail, y1 + pyp * w_tail);
    ctx.lineTo(indentX + pxp * w_head, indentY + pyp * w_head);
    ctx.lineTo(indentX - pxp * w_head, indentY - pyp * w_head);
    ctx.lineTo(x1 - pxp * w_tail, y1 - pyp * w_tail);
    ctx.closePath();
    ctx.fill();

    // Round tail cap
    ctx.beginPath();
    ctx.arc(x1, y1, w_tail, 0, Math.PI * 2);
    ctx.fill();

    // Draw swept-back (chevron) arrowhead
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - hl * Math.cos(angle) + hw * Math.sin(angle), y2 - hl * Math.sin(angle) - hw * Math.cos(angle));
    ctx.lineTo(indentX, indentY);
    ctx.lineTo(x2 - hl * Math.cos(angle) - hw * Math.sin(angle), y2 - hl * Math.sin(angle) + hw * Math.cos(angle));
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  } else if (obj.type === "circle") {
    const rx = Math.abs(obj.x2 - obj.x1) / 2, ry = Math.abs(obj.y2 - obj.y1) / 2;
    ctx.save();
    ctx.lineWidth = (obj.thickness || 3) * 1.5;
    ctx.beginPath(); ctx.ellipse((obj.x1 + obj.x2) / 2, (obj.y1 + obj.y2) / 2, rx, ry, 0, 0, Math.PI * 2);
    if (obj.filled) { ctx.fillStyle = obj.fillColor || obj.color; ctx.fill(); ctx.fillStyle = obj.color; }
    ctx.stroke();
    ctx.restore();
  } else if (obj.type === "rect") {
    ctx.save();
    ctx.lineWidth = (obj.thickness || 3) * 1.5;
    if (obj.filled) { ctx.fillStyle = obj.fillColor || obj.color; ctx.fillRect(obj.x1, obj.y1, obj.x2 - obj.x1, obj.y2 - obj.y1); ctx.fillStyle = obj.color; }
    ctx.strokeRect(obj.x1, obj.y1, obj.x2 - obj.x1, obj.y2 - obj.y1);
    ctx.restore();
  } else if (obj.type === "blur") {
    const rx1 = Math.min(obj.x1, obj.x2), ry1 = Math.min(obj.y1, obj.y2);
    const rx2 = Math.max(obj.x1, obj.x2), ry2 = Math.max(obj.y1, obj.y2);
    const rw = rx2 - rx1;
    const rh = ry2 - ry1;

    ctx.save();
    ctx.beginPath();
    ctx.rect(rx1, ry1, rw, rh);
    ctx.clip();

    const { imgEl, imgSize, adjustments } = options || {};
    if (imgEl) {
      const adj = adjustments || { brightness: 100, contrast: 100, saturation: 100 };
      const iw = imgSize ? imgSize.w : ctx.canvas.width;
      const ih = imgSize ? imgSize.h : ctx.canvas.height;
      ctx.filter = `brightness(${adj.brightness}%) contrast(${adj.contrast}%) saturate(${adj.saturation}%) blur(14px)`;
      ctx.drawImage(imgEl, 0, 0, iw, ih);
    }
    ctx.restore();
  } else if (obj.type === "spotlight") {
    const rx1 = Math.min(obj.x1, obj.x2), ry1 = Math.min(obj.y1, obj.y2);
    const rx2 = Math.max(obj.x1, obj.x2), ry2 = Math.max(obj.y1, obj.y2);
    const rw = rx2 - rx1;
    const rh = ry2 - ry1;
    const cx = rx1 + rw / 2;
    const cy = ry1 + rh / 2;
    const radius = Math.max(0.1, Math.max(rw, rh) / 2);

    const { imgSize, objects } = options || {};
    const iw = imgSize ? imgSize.w : ctx.canvas.width;
    const ih = imgSize ? imgSize.h : ctx.canvas.height;

    // Draw dark overlay ONCE for all spotlights combined using an offscreen canvas
    // to correctly punch out multiple non-overlapping holes.
    const allSpotlights = (objects || []).filter(o => o.type === "spotlight");
    if (allSpotlights.length === 0 || allSpotlights[0].id === obj.id) {
      const tCanvas = document.createElement("canvas");
      tCanvas.width = iw;
      tCanvas.height = ih;
      const tCtx = tCanvas.getContext("2d");
      
      tCtx.fillStyle = "rgba(0, 0, 0, 0.65)";
      tCtx.fillRect(0, 0, iw, ih);
      
      tCtx.globalCompositeOperation = "destination-out";
      tCtx.fillStyle = "#000000"; // Fully opaque (alpha=1.0) ensures 100% clean transparent hole
      allSpotlights.forEach(s => {
        const srw = Math.max(s.x1, s.x2) - Math.min(s.x1, s.x2);
        const srh = Math.max(s.y1, s.y2) - Math.min(s.y1, s.y2);
        const sRadius = Math.max(0.1, Math.max(srw, srh) / 2);
        const scx = Math.min(s.x1, s.x2) + srw / 2;
        const scy = Math.min(s.y1, s.y2) + srh / 2;
        tCtx.beginPath();
        tCtx.ellipse(scx, scy, sRadius, sRadius, 0, 0, Math.PI * 2);
        tCtx.fill();
      });
      
      ctx.save();
      ctx.drawImage(tCanvas, 0, 0);
      ctx.restore();
    }

    // Draw the bright border ring for this specific spotlight
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(cx, cy, radius, radius, 0, 0, Math.PI * 2);
    ctx.lineWidth = (obj.thickness || 3) * 1.5;
    ctx.strokeStyle = obj.color || "#ffcc00";
    ctx.stroke();
    ctx.restore();
  } else if (obj.type === "text") {
    const textStr = obj.text || "";
    const dx = obj.x2 - obj.x1;
    const dy = obj.y2 - obj.y1;
    const len = Math.hypot(dx, dy) || 1;
    const ang = Math.atan2(dy, dx);
    const rawLines = textStr.split("\n");
    const maxLineChars = Math.max(...rawLines.map(l => l.length), 1);
    const fs = obj.fontSize || Math.max(10, Math.min(200, len / (maxLineChars * 0.55)));

    ctx.save();
    ctx.translate(obj.x1, obj.y1);
    ctx.rotate(ang);
    ctx.font = `${obj.bold === false ? "" : "bold "}${fs}px -apple-system, sans-serif`;

    const wrapWidth = Math.max(len, 100);
    const lines = getWrappedTextLines(ctx, textStr, wrapWidth);
    const lineHeight = fs * 1.3;

    const align = obj.align || "left";
    ctx.textAlign = align;
    let tx = 0;
    if (align === "center") tx = len / 2;
    else if (align === "right") tx = len;

    const lineMetrics = lines.map(line => ({
      text: line,
      width: ctx.measureText(line).width
    }));

    // Draw Highlight Background
    if (obj.highlight) {
      const hlColor = obj.highlightColor || "#ffff00";
      const padX = fs * 0.22;
      const padY = fs * 0.15;
      const boxH = fs + padY * 2;

      ctx.save();
      ctx.fillStyle = hlColor;
      ctx.shadowColor = "transparent";

      lines.forEach((line, i) => {
        if (!line.trim()) return;
        const lw = lineMetrics[i].width;
        let bx = tx;
        if (align === "center") bx = tx - lw / 2;
        else if (align === "right") bx = tx - lw;

        const by = i * lineHeight - fs * 0.5 - padY;
        const bw = lw + padX * 2;
        const radius = Math.min(4, padX);

        ctx.beginPath();
        if (typeof ctx.roundRect === "function") {
          ctx.roundRect(bx - padX, by, bw, boxH, radius);
        } else {
          ctx.rect(bx - padX, by, bw, boxH);
        }
        ctx.fill();
      });
      ctx.restore();
    }

    // Draw Text
    ctx.fillStyle = obj.color;
    ctx.textBaseline = "middle";
    if (obj.highlight) {
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
    } else {
      ctx.shadowColor = "rgba(0,0,0,0.38)";
      ctx.shadowBlur = 4.0;
      ctx.shadowOffsetX = 1.5;
      ctx.shadowOffsetY = 1.5;
    }

    lines.forEach((line, i) => {
      ctx.fillText(line, tx, i * lineHeight);
    });
    ctx.restore();
  } else if (obj.type === "timestamp") {
    const fs = obj.fontSize || 22;
    ctx.font = `${obj.bold === false ? "" : "bold "}${fs}px -apple-system, sans-serif`;
    const lines = obj.lines || [obj.text];
    const widths = lines.map(l => ctx.measureText(l).width);
    const maxW = Math.max(...widths, 0);
    const align = obj.align || "center";
    lines.forEach((line, i) => {
      let lx = obj.x;
      if (align === "center") lx = obj.x - maxW / 2 + (maxW - widths[i]) / 2;
      else if (align === "right") lx = obj.x - maxW + (maxW - widths[i]);
      ctx.fillText(line, lx, obj.y + i * (fs + 4));
    });
  } else if (obj.type === "sticker") {
    const s = obj.stickerSize || 36;
    if (obj.img && obj.img.complete && obj.img.naturalWidth > 0) {
      ctx.drawImage(obj.img, obj.x - s, obj.y - s, s * 2, s * 2);
    } else if (obj.img && !obj.img.complete) {
      // Image is still loading in background; onload will trigger canvas redraw
    } else {
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      if (obj.sid === "logo") {
        ctx.fillStyle = "#1a3a5c"; ctx.beginPath(); ctx.arc(obj.x, obj.y, s, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#fff"; ctx.font = `bold ${s * 0.55}px sans-serif`; ctx.fillText("FD", obj.x, obj.y);
      } else {
        const fallbackEmoji = {
          warning: "⚠️",
          checkmark: "✔",
          check: "✔",
          check_green: "✔",
          cross: "✗",
          x_red: "✗",
          thumbs_up: "👍",
          thumbs_down: "👎",
          approved: "✅",
          finished: "🏁",
          start: "🚩",
          end: "🏁",
          red_dot: "🔴",
          green_dot: "🟢",
          yellow_dot: "🟡",
          blue_dot: "🔵",
        }[obj.sid] || "";
        if (fallbackEmoji) {
          ctx.font = `${s * 1.5}px sans-serif`;
          ctx.fillText(fallbackEmoji, obj.x, obj.y);
        }
      }
    }
  }
  ctx.restore();

  // Draw the text label in unrotated screen coordinates (world space)
  if (obj.type !== "text" && obj.type !== "timestamp" && obj.type !== "magnifier" && obj.text) {
    ctx.save();
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
    const fs = obj.fontSize || 22;
    ctx.font = `${obj.bold === false ? "" : "bold "}${fs}px -apple-system, sans-serif`;
    ctx.fillStyle = obj.color;
    const lines = obj.lines || [obj.text];
    const widths = lines.map(l => ctx.measureText(l).width);
    const maxW = Math.max(...widths, 0);
    const align = obj.align || "center";
    const anchor = labelAnchor(obj, align);
    lines.forEach((line, i) => {
      let lx = anchor.x; // default left align
      if (align === "center") lx = anchor.x - maxW / 2 + (maxW - widths[i]) / 2;
      else if (align === "right") lx = anchor.x - maxW + (maxW - widths[i]);
      ctx.fillText(line, lx, anchor.y + i * (fs + 4));
    });
    ctx.restore();
  }

  // Draw any embedded magnifier lenses AFTER restore (not affected by rotation ctx)
  if (["arrow", "line", "rect", "circle", "blur", "spotlight"].includes(obj.type)) {
    drawEmbeddedMagnifiers(ctx, obj, options);
  }
}

function localToWorld(obj, localPt) {
  const rot = obj.rotation || 0;
  if (!rot) return localPt;
  const b = getBounds(obj);
  if (!b) return localPt;

  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  const rad = rot * Math.PI / 180;
  const dx = localPt.x - cx;
  const dy = localPt.y - cy;

  return {
    x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
    y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
  };
}

export function labelAnchor(obj, align = "center") {
  const fs = obj.fontSize || 22;

  if (obj.type === "line" || obj.type === "arrow") {
    const mWorld = localToWorld(obj, { x: obj.x1, y: obj.y1 });
    const p2World = localToWorld(obj, { x: obj.x2, y: obj.y2 });
    const isPointingDown = p2World.y > mWorld.y + 10;
    const textY = isPointingDown ? mWorld.y - 14 : mWorld.y + fs + 14;
    return { x: mWorld.x, y: textY };
  }

  if (["rect", "circle", "blur", "spotlight"].includes(obj.type)) {
    const rx1 = Math.min(obj.x1, obj.x2), ry1 = Math.min(obj.y1, obj.y2);
    const rx2 = Math.max(obj.x1, obj.x2), ry2 = Math.max(obj.y1, obj.y2);
    const corners = [
      localToWorld(obj, { x: rx1, y: ry1 }),
      localToWorld(obj, { x: rx2, y: ry1 }),
      localToWorld(obj, { x: rx1, y: ry2 }),
      localToWorld(obj, { x: rx2, y: ry2 }),
    ];
    const xs = corners.map(c => c.x);
    const ys = corners.map(c => c.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    let anchorX = (minX + maxX) / 2;
    if (align === "left") anchorX = minX;
    else if (align === "right") anchorX = maxX;
    const maxY = Math.max(...ys);
    return { x: anchorX, y: maxY + fs + 6 };
  }

  if (obj.type === "pen" && obj.points?.length) {
    const ptsWorld = obj.points.map(p => localToWorld(obj, p));
    const xs = ptsWorld.map(p => p.x);
    const ys = ptsWorld.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    let anchorX = (minX + maxX) / 2;
    if (align === "left") anchorX = minX;
    else if (align === "right") anchorX = maxX;
    const maxY = Math.max(...ys);
    return { x: anchorX, y: maxY + fs + 6 };
  }

  if (obj.type === "sticker") {
    const s = obj.stickerSize || 36;
    const centerWorld = localToWorld(obj, { x: obj.x, y: obj.y });
    return { x: centerWorld.x, y: centerWorld.y + s + fs };
  }

  return { x: 0, y: 0 };
}

export function getBounds(obj) {
  if (obj.type === "magnifier") {
    const r = obj.lensRadius || 70;
    const pts = [
      { x: obj.anchorX ?? 0, y: obj.anchorY ?? 0 },
      { x: obj.targetX ?? 0, y: obj.targetY ?? 0 },
      { x: (obj.lensX ?? 0) - r, y: (obj.lensY ?? 0) - r },
      { x: (obj.lensX ?? 0) + r, y: (obj.lensY ?? 0) + r },
    ];
    const xs = pts.map(p => p.x);
    const ys = pts.map(p => p.y);
    return { x: Math.min(...xs) - 8, y: Math.min(...ys) - 8, w: Math.max(...xs) - Math.min(...xs) + 16, h: Math.max(...ys) - Math.min(...ys) + 16 };
  }
  if (obj.type === "pen") {
    if (!obj.points?.length) return null;
    const xs = obj.points.map(p => p.x), ys = obj.points.map(p => p.y);
    return { x: Math.min(...xs) - 10, y: Math.min(...ys) - 10, w: Math.max(...xs) - Math.min(...xs) + 20, h: Math.max(...ys) - Math.min(...ys) + 20 };
  }
  if (obj.type === "arrow" || obj.type === "line") {
    // Expand bounds to include any lens circles
    let pts = [{ x: obj.x1, y: obj.y1 }, { x: obj.x2, y: obj.y2 }];
    for (const [, magKey] of Object.entries(HANDLE_TO_MAG_KEY)) {
      const mag = obj[magKey];
      if (!mag) continue;
      const r = mag.lensRadius || 70;
      pts.push({ x: mag.lensX - r, y: mag.lensY - r });
      pts.push({ x: mag.lensX + r, y: mag.lensY + r });
    }
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
    return { x: Math.min(...xs) - 12, y: Math.min(...ys) - 12, w: Math.max(...xs) - Math.min(...xs) + 24, h: Math.max(...ys) - Math.min(...ys) + 24 };
  }
  if (["rect", "circle", "blur", "spotlight"].includes(obj.type)) {
    let pts = [{ x: obj.x1, y: obj.y1 }, { x: obj.x2, y: obj.y2 }];
    for (const [, magKey] of Object.entries(HANDLE_TO_MAG_KEY)) {
      const mag = obj[magKey];
      if (!mag) continue;
      const r = mag.lensRadius || 70;
      pts.push({ x: mag.lensX - r, y: mag.lensY - r });
      pts.push({ x: mag.lensX + r, y: mag.lensY + r });
    }
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
    return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
  }
  if (obj.type === "text") {
    const dx = obj.x2 - obj.x1;
    const dy = obj.y2 - obj.y1;
    const len = Math.hypot(dx, dy) || 1;
    const textStr = obj.text || "";
    const rawLines = textStr.split("\n");
    const maxLineChars = Math.max(...rawLines.map(l => l.length), 1);
    const fs = obj.fontSize || Math.max(10, Math.min(200, len / (maxLineChars * 0.55)));
    const lineCount = Math.max(rawLines.length, 1);
    const lineHeight = fs * 1.3;
    const totalH = lineCount * lineHeight;
    return { x: obj.x1, y: obj.y1 - fs / 2, w: len, h: totalH };
  }
  if (obj.type === "timestamp") {
    const fs = obj.fontSize || 22; const lines = obj.lines || [obj.text];
    const approxW = Math.max(...lines.map(l => (l||"").length)) * fs * 0.62;
    const align = obj.align || "center";
    let bx = obj.x;
    if (align === "center") bx = obj.x - approxW / 2;
    else if (align === "right") bx = obj.x - approxW;
    return { x: bx - 4, y: obj.y - fs - 4, w: approxW + 8, h: lines.length * (fs + 4) + 8 };
  }
  if (obj.type === "sticker") { const s = (obj.stickerSize || 36) + 10; return { x: obj.x - s, y: obj.y - s, w: s * 2, h: s * 2 }; }
  return null;
}

export function drawHandle(ctx, x, y, color, r) {
  ctx.save();
  const radius = r ? r - 1.5 : 5.5;
  // Outer circle (thick colored border ring)
  ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = color; ctx.fill();
  
  // Inner circle (small white dot)
  ctx.beginPath(); ctx.arc(x, y, radius - 2.5, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff"; ctx.fill();
  ctx.restore();
}

// Small lens-indicator handle drawn ON TOP of an active endpoint handle
function drawLensHandle(ctx, x, y, color) {
  ctx.save();
  ctx.beginPath(); ctx.arc(x, y, 11, 0, Math.PI * 2);
  ctx.fillStyle = color; ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.lineWidth = 1.5; ctx.stroke();
  // "+" cross inside
  ctx.strokeStyle = "#fff"; ctx.lineWidth = 1.8; ctx.lineCap = "round";
  ctx.beginPath(); ctx.moveTo(x - 4, y); ctx.lineTo(x + 4, y); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(x, y - 4); ctx.lineTo(x, y + 4); ctx.stroke();
  // tiny circle
  ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.7)"; ctx.lineWidth = 1; ctx.stroke();
  ctx.restore();
}

export function drawSelection(ctx, obj) {
  if (obj.type === "magnifier") {
    const r = obj.lensRadius || 70;
    const color = obj.color || "#ffcc00";
    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(obj.lensX, obj.lensY, r + 5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    drawHandle(ctx, obj.anchorX, obj.anchorY, color, 7);
    drawHandle(ctx, obj.targetX, obj.targetY, color, 7);
    drawHandle(ctx, obj.lensX + r, obj.lensY, color, 7);
    ctx.restore();
    return;
  }

  if (obj.type === "timestamp" || obj.type === "sticker") {
    const b = getBounds(obj);
    if (!b) return;
    ctx.save();
    ctx.strokeStyle = obj.color;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(b.x, b.y, b.w, b.h);
    ctx.setLineDash([]);

    // Draw all 4 resize/rotate corners
    drawHandle(ctx, b.x, b.y, obj.color, 7); // tl
    drawHandle(ctx, b.x + b.w, b.y, obj.color, 7); // tr
    drawHandle(ctx, b.x, b.y + b.h, obj.color, 7); // bl
    drawHandle(ctx, b.x + b.w, b.y + b.h, obj.color, 7); // br
    ctx.restore();
    return;
  }

  if (!["arrow", "line", "rect", "circle", "text", "blur", "spotlight"].includes(obj.type)) return;

  const b = getBounds(obj);
  if (!b) return;

  const rot = obj.type === "text" ? 0 : (obj.rotation || 0);
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;

  ctx.save();
  if (rot) {
    ctx.translate(cx, cy);
    ctx.rotate(rot * Math.PI / 180);
    ctx.translate(-cx, -cy);
  }
  ctx.strokeStyle = obj.color;
  ctx.lineWidth = 1.5;

  if (obj.type === "arrow" || obj.type === "line" || obj.type === "text") {
    [[obj.x1, obj.y1, "p1"], [obj.x2, obj.y2, "p2"]].forEach(([x, y, hk]) => {
      drawHandle(ctx, x, y, obj.color, 8);
      // If this endpoint has a magnifier, draw the lens-indicator on the handle
      const magKey = HANDLE_TO_MAG_KEY[hk];
      if (magKey && obj[magKey]) {
        drawLensHandle(ctx, x, y, obj.color);
        // Also draw a dashed ring around the lens bubble
        const mag = obj[magKey];
        const r = mag.lensRadius || 70;
        ctx.save();
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = obj.color;
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(mag.lensX, mag.lensY, r + 5, 0, Math.PI * 2); ctx.stroke();
        ctx.setLineDash([]);
        // drag handle on lens edge (right side)
        drawHandle(ctx, mag.lensX + r, mag.lensY, obj.color, 6);
        ctx.restore();
      }
    });
  } else {
    ctx.strokeStyle = obj.color;
    ctx.lineWidth = 1.5;
    // Use raw x1/x2/y1/y2 for the selection rect so it doesn't grow with lens bounds
    const rx1 = Math.min(obj.x1, obj.x2), ry1 = Math.min(obj.y1, obj.y2);
    const rx2 = Math.max(obj.x1, obj.x2), ry2 = Math.max(obj.y1, obj.y2);
    const rb = { x: rx1, y: ry1, w: rx2 - rx1, h: ry2 - ry1 };
    ctx.strokeRect(rb.x, rb.y, rb.w, rb.h);
    const corners = [
      { x: rb.x, y: rb.y, hk: "tl" },
      { x: rb.x + rb.w, y: rb.y, hk: "tr" },
      { x: rb.x, y: rb.y + rb.h, hk: "bl" },
      { x: rb.x + rb.w, y: rb.y + rb.h, hk: "br" },
    ];
    corners.forEach(({ x, y, hk }) => {
      drawHandle(ctx, x, y, obj.color, 7);
      const magKey = HANDLE_TO_MAG_KEY[hk];
      if (magKey && obj[magKey]) {
        drawLensHandle(ctx, x, y, obj.color);
        const mag = obj[magKey];
        const r = mag.lensRadius || 70;
        ctx.save();
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = obj.color;
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(mag.lensX, mag.lensY, r + 5, 0, Math.PI * 2); ctx.stroke();
        ctx.setLineDash([]);
        drawHandle(ctx, mag.lensX + r, mag.lensY, obj.color, 6);
        ctx.restore();
      }
    });
  }

  ctx.restore();
}
